// contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        id: null,
        token: null,
        user: null,
        isAuthenticated: false
    });

    // Initialize auth state from localStorage/sessionStorage on app start
    useEffect(() => {
        const initializeAuth = () => {
            try {
                const storedAuth = localStorage.getItem('auth');
                if (storedAuth) {
                    const parsedAuth = JSON.parse(storedAuth);
                    setAuthState({
                        id: parsedAuth.id,
                        token: parsedAuth.token,
                        user: parsedAuth.user,
                        isAuthenticated: true
                    });
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
                // Clear corrupted storage
                localStorage.removeItem('auth');
            }
        };

        initializeAuth();
    }, []);

    // Login function
    const login = (userData) => {
        const authData = {
            id: userData.id,
            token: userData.token,
            user: userData.user,
            isAuthenticated: true
        };

        setAuthState(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
    };

    // Logout function
    const logout = () => {
        setAuthState({
            id: null,
            token: null,
            user: null,
            isAuthenticated: false
        });
        localStorage.removeItem('auth');
    };

    // Update user data
    const updateUser = (userData) => {
        const updatedAuth = {
            ...authState,
            user: { ...authState.user, ...userData }
        };
        setAuthState(updatedAuth);
        localStorage.setItem('auth', JSON.stringify(updatedAuth));
    };

    const value = {
        authState,
        login,
        logout,
        updateUser,
        isAuthenticated: authState.isAuthenticated
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Keep the default export for backward compatibility
export { AuthContext };